import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Cloud, Loader2, RefreshCw, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth";
import {
  listOfflineSales,
  OFFLINE_SALES_EVENT,
  OFFLINE_SALES_ERROR_EVENT,
  OFFLINE_SALES_QUEUE_ERROR_MESSAGE,
  retryOfflineSale,
  summarizeOfflineSales,
  syncOfflineSales,
  shouldShowOfflineSalesStatus,
  type OfflineSale,
} from "@/lib/offline-sales";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export function OfflineSyncStatus() {
  const { store, user, offlineRestored } = useAuth();
  const queryClient = useQueryClient();
  const [sales, setSales] = useState<OfflineSale[]>([]);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const scopeKey = store?.id && user?.id ? `${store.id}:${user.id}` : "";
  const activeScopeKey = useRef(scopeKey);
  activeScopeKey.current = scopeKey;

  const refreshSales = useCallback(async () => {
    if (!store?.id || !user?.id) {
      setSales([]);
      return;
    }
    const requestedScopeKey = `${store.id}:${user.id}`;
    try {
      const nextSales = await listOfflineSales(store.id, user.id);
      if (activeScopeKey.current === requestedScopeKey) setSales(nextSales);
    } catch (error) {
      console.error("Could not load queued offline sales:", error);
      if (activeScopeKey.current === requestedScopeKey) {
        setQueueError(OFFLINE_SALES_QUEUE_ERROR_MESSAGE);
      }
    }
  }, [store?.id, user?.id]);

  useEffect(() => {
    setSales([]);
    setQueueError(null);
    const onOnline = () => {
      setIsOnline(true);
      void refreshSales();
    };
    const onOffline = () => setIsOnline(false);
    const onQueueUpdate = () => { void refreshSales(); };
    const onQueueError = () => setQueueError(OFFLINE_SALES_QUEUE_ERROR_MESSAGE);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener(OFFLINE_SALES_EVENT, onQueueUpdate);
    window.addEventListener(OFFLINE_SALES_ERROR_EVENT, onQueueError);
    void refreshSales();

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener(OFFLINE_SALES_EVENT, onQueueUpdate);
      window.removeEventListener(OFFLINE_SALES_ERROR_EVENT, onQueueError);
    };
  }, [refreshSales]);

  useEffect(() => {
    if (sales.length === 0 && !queueError) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [sales.length, queueError]);

  const handleSync = async (retryReference?: string) => {
    if (!store?.id || !user?.id || !navigator.onLine || offlineRestored || isSyncing) return;
    setIsSyncing(true);
    setQueueError(null);
    try {
      if (retryReference) await retryOfflineSale(retryReference);
      const summary = await syncOfflineSales(store.id, user.id, BASE);
      await refreshSales();
      setQueueError(null);
      if (summary.total === 0) await queryClient.invalidateQueries();
    } catch (error) {
      console.error("Could not synchronize queued offline sales:", error);
      await refreshSales();
      setQueueError(OFFLINE_SALES_QUEUE_ERROR_MESSAGE);
    } finally {
      setIsSyncing(false);
    }
  };

  if (!store?.id || !user?.id) return null;

  const summary = summarizeOfflineSales(sales);
  if (!shouldShowOfflineSalesStatus(isOnline, summary, Boolean(queueError))) return null;

  const hasAttention = summary.needsAttention > 0 || Boolean(queueError);
  const hasActiveSync = summary.syncing > 0 || isSyncing;
  const pendingFailures = sales.filter(sale => sale.status === "pending" && sale.error);
  const statusTitle = queueError
    ? "Offline sales status needs attention"
    : !isOnline
    ? "Offline mode"
    : hasAttention
      ? `${summary.needsAttention} ${summary.needsAttention === 1 ? "sale needs" : "sales need"} attention`
      : hasActiveSync
        ? `Syncing ${summary.syncing || summary.pending} queued ${summary.syncing + summary.pending === 1 ? "sale" : "sales"}`
        : pendingFailures.length > 0
          ? `${pendingFailures.length} ${pendingFailures.length === 1 ? "sale couldn't" : "sales couldn't"} sync yet`
          : `${summary.pending} ${summary.pending === 1 ? "sale" : "sales"} waiting to sync`;

  const statusDescription = queueError
    ? queueError
    : !isOnline
    ? summary.total > 0
      ? `${summary.pending + summary.syncing} waiting to sync · ${summary.needsAttention} need attention. Cash and eligible credit sales can still be saved.`
      : "Cash and eligible credit sales can be saved. Online payment methods are unavailable."
    : hasAttention
      ? "Review the message below. Sales needing attention will not retry automatically."
      : hasActiveSync
        ? "Queued sales are being sent to RetailPulse."
        : pendingFailures.length > 0
          ? "The last sync attempt failed. Use Sync now to retry."
        : "These sales are saved on this device and will sync to RetailPulse.";

  return (
    <div
      role={hasAttention ? "alert" : "status"}
      aria-live={hasAttention ? "assertive" : "polite"}
      className={`flex-shrink-0 border-b px-4 py-3 md:px-6 ${
        hasAttention
          ? "border-destructive/30 bg-destructive/5"
          : "border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          {!isOnline
            ? <WifiOff className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
            : hasAttention
              ? <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
              : hasActiveSync
                ? <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin text-primary" />
                : <Cloud className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />}
          <div className="min-w-0">
            <p className="text-sm font-semibold">{statusTitle}</p>
            <p className="text-sm text-muted-foreground">{statusDescription}</p>
          </div>
        </div>
        {isOnline && (summary.pending > 0 || Boolean(queueError)) && !hasActiveSync && (
          <Button size="sm" variant="outline" onClick={() => void handleSync()} disabled={isSyncing || offlineRestored}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {queueError ? "Retry check" : "Sync now"}
          </Button>
        )}
      </div>

      {pendingFailures.map(sale => (
        <div
          key={sale.clientReference}
          className="mt-3 rounded-lg border border-amber-300 bg-background p-3 dark:border-amber-900"
        >
          <p className="truncate text-sm font-medium">
            {sale.receipt.receiptNumber} · ₦{sale.receipt.total.toLocaleString("en-NG")}
          </p>
          <p className="text-xs text-amber-800 dark:text-amber-200">
            {sale.error}
            {!isOnline && " It will retry when the connection returns."}
          </p>
        </div>
      ))}

      {sales.filter(sale => sale.status === "needs_attention").map(sale => (
        <div
          key={sale.clientReference}
          className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-background p-3"
        >
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">
              {sale.receipt.receiptNumber} · ₦{sale.receipt.total.toLocaleString("en-NG")}
            </p>
            <p className="text-xs text-destructive">{sale.error ?? "Review this sale before retrying."}</p>
          </div>
            {isOnline ? (
              <Button size="sm" variant="outline" onClick={() => void handleSync(sale.clientReference)} disabled={isSyncing || offlineRestored}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">Reconnect to retry</span>
          )}
        </div>
      ))}
    </div>
  );
}